import React, { useState, useCallback, useMemo, useRef, useEffect } from 'react';
import { AgGridReact } from 'ag-grid-react';
import { AllCommunityModule, ModuleRegistry } from 'ag-grid-community';
import { Switch, message, Tooltip, Modal, InputNumber, Select, Input, Popover, Checkbox } from 'antd';
import { EditOutlined, InfoCircleOutlined, CalculatorOutlined, FormOutlined, EyeOutlined } from '@ant-design/icons';
import PatientService from '../../../services/PatientService';
import { useNavigate } from 'react-router-dom';
import ROUTES from '../../../constants/routes';
import { EGFR_MIN, EGFR_MAX, EGFR_RANGE_MESSAGE, isEgfrOutOfRange } from '../../PatientQueue/PatientEditView/utils/constants';
import { updateSpreadsheetEgfr } from '../utils/spreadsheetEgfr';
import './SpreadsheetView.css';

// Register AG Grid modules
ModuleRegistry.registerModules([AllCommunityModule]);

// localStorage key for column visibility
const COL_VISIBILITY_KEY = 'bha_spreadsheet_col_visibility';

// Column group definitions for the column picker
const COLUMN_GROUPS = [
  {
    group: 'Member Info',
    columns: [
      { field: 'phone', label: 'Phone' },
      { field: 'age', label: 'Age' },
      { field: 'gender', label: 'Gender' },
      { field: 'testingCode', label: 'Testing Code' },
    ],
  },
  {
    group: 'Vital Signs',
    columns: [
      { field: 'bloodPressure', label: 'BP' },
      { field: 'heartRate', label: 'HR' },
    ],
  },
  {
    group: 'Blood Work',
    columns: [
      { field: 'totalCholesterol', label: 'Cholesterol' },
      { field: 'hdlCholesterol', label: 'HDL' },
      { field: 'ldlCholesterol', label: 'LDL' },
      { field: 'triglycerides', label: 'Triglycerides' },
      { field: 'eGFR', label: 'eGFR' },
      { field: 'bloodGlucose', label: 'Glucose' },
      { field: 'a1c', label: 'A1C' },
      { field: 'lipoproteinA', label: 'Lp(a)' },
      { field: 'fastingStatus', label: 'Fasting' },
    ],
  },
  {
    group: 'EKG',
    columns: [
      { field: 'heartRhythm', label: 'Rhythm' },
      { field: 'qrsDuration', label: 'QRS' },
      { field: 'prInterval', label: 'PR Interval' },
      { field: 'qtcInterval', label: 'QTc' },
      { field: 'ekgInterpretation', label: 'Interpretation' },
    ],
  },
  {
    group: 'Body',
    columns: [
      { field: 'height', label: 'Height' },
      { field: 'weight', label: 'Weight' },
      { field: 'whr', label: 'WHR' },
    ],
  },
  {
    group: 'Status',
    columns: [
      { field: 'riskLevel', label: 'Risk' },
      { field: 'status', label: 'Status' },
      { field: 'eventTitle', label: 'Event' },
      { field: 'bloodTestHandlerName', label: 'Handler' },
    ],
  },
];

// Helper: check if a value is "missing"
const isMissing = (val) => {
  if (val === null || val === undefined || val === '') return true;
  if (typeof val === 'string') {
    const lower = val.toLowerCase().trim();
    return ['not recorded', 'not tested', 'not performed', 'not measured', 'not provided', 'not specified', 'not calculated', 'n/a', 'unknown', '—'].includes(lower);
  }
  return false;
};

// Critical fields - must have data for proper screening
const CRITICAL_FIELDS = [
  'bloodPressure', 'heartRate', 'totalCholesterol', 'hdlCholesterol',
  'ldlCholesterol', 'bloodGlucose'
];

// Recommended fields - nice to have
const RECOMMENDED_FIELDS = [
  'a1c', 'triglycerides', 'lipoproteinA', 'heartRhythm',
  'ekgInterpretation', 'weight', 'height', 'whr'
];

// Cell class rules for empty cell highlighting
const makeCellClassRules = (field) => ({
  'cell-missing-critical': (params) => isMissing(params.value) && CRITICAL_FIELDS.includes(field),
  'cell-missing-recommended': (params) => isMissing(params.value) && RECOMMENDED_FIELDS.includes(field),
  'cell-has-data': (params) => !isMissing(params.value),
});

// Status options for dropdown
const STATUS_OPTIONS = [
  'completed', 'in_progress', 'pending_review', 'archived',
  'new_patient', 'matched', 'needs_attention'
];

// Gender options
const GENDER_OPTIONS = ['Male', 'Female', 'Non-binary', 'Not specified'];

// EKG rhythm options (matching edit page values)
const RHYTHM_OPTIONS = ['normal_sinus', 'atrial_fibrillation', 'atrial_flutter', 'ventricular_tachycardia', 'bradycardia', 'other'];

// Rhythm display labels
const RHYTHM_LABELS = {
  normal_sinus: 'Normal Sinus',
  atrial_fibrillation: 'Atrial Fibrillation',
  atrial_flutter: 'Atrial Flutter',
  ventricular_tachycardia: 'V-Tach',
  bradycardia: 'Bradycardia',
  other: 'Other',
};

// Validation rules matching the edit page (MedicalTestingTab)
const FIELD_VALIDATION = {
  bloodPressure: {
    validate: (val) => {
      const str = String(val).trim();
      const parts = str.split('/');
      if (parts.length !== 2) return 'Enter as SYS/DIA (e.g. 120/80)';
      const sys = Number(parts[0].trim());
      const dia = Number(parts[1].trim());
      if (isNaN(sys) || isNaN(dia)) return 'Must be numbers (e.g. 120/80)';
      if (sys < 50 || sys > 300) return 'Systolic must be 50-300';
      if (dia < 30 || dia > 200) return 'Diastolic must be 30-200';
      return null;
    },
    parse: (val) => {
      const parts = String(val).split('/');
      return `${parts[0].trim()}/${parts[1].trim()}`;
    },
    tooltip: 'SYS/DIA mmHg (e.g. 120/80)',
  },
  heartRate: {
    validate: (val) => {
      const n = Number(String(val).replace(/\s*bpm/i, ''));
      if (isNaN(n)) return 'Must be a number';
      if (n < 30 || n > 250) return 'Must be 30-250 bpm';
      return null;
    },
    parse: (val) => String(val).replace(/\s*bpm/i, '').trim(),
    tooltip: 'bpm (30-250)',
  },
  totalCholesterol: {
    validate: (val) => { const n = Number(val); if (isNaN(n)) return 'Must be a number'; if (n < 50 || n > 500) return '50-500 mg/dL'; return null; },
    parse: (val) => String(val).replace(/[^\d.]/g, ''),
    tooltip: 'mg/dL (50-500)',
  },
  hdlCholesterol: {
    validate: (val) => { const n = Number(val); if (isNaN(n)) return 'Must be a number'; if (n < 10 || n > 200) return '10-200 mg/dL'; return null; },
    parse: (val) => String(val).replace(/[^\d.]/g, ''),
    tooltip: 'mg/dL (10-200)',
  },
  ldlCholesterol: {
    validate: (val) => { const n = Number(val); if (isNaN(n)) return 'Must be a number'; if (n < 10 || n > 400) return '10-400 mg/dL'; return null; },
    parse: (val) => String(val).replace(/[^\d.]/g, ''),
    tooltip: 'mg/dL (10-400)',
  },
  triglycerides: {
    validate: (val) => { const n = Number(val); if (isNaN(n)) return 'Must be a number'; if (n < 10 || n > 1000) return '10-1000 mg/dL'; return null; },
    parse: (val) => String(val).replace(/[^\d.]/g, ''),
    tooltip: 'mg/dL (10-1000)',
  },
  bloodGlucose: {
    validate: (val) => { const n = Number(val); if (isNaN(n)) return 'Must be a number'; if (n < 30 || n > 500) return '30-500 mg/dL'; return null; },
    parse: (val) => String(val).replace(/[^\d.]/g, ''),
    tooltip: 'mg/dL (30-500)',
  },
  a1c: {
    validate: (val) => { const n = Number(val); if (isNaN(n)) return 'Must be a number'; if (n < 3 || n > 20) return '3-20 %'; return null; },
    parse: (val) => String(val).replace(/[^\d.]/g, ''),
    tooltip: '% (3-20)',
  },
  lipoproteinA: {
    validate: (val) => { const n = Number(val); if (isNaN(n)) return 'Must be a number'; if (n < 0 || n > 500) return '0-500 nmol/L'; return null; },
    parse: (val) => String(val).replace(/[^\d.]/g, ''),
    tooltip: 'nmol/L (0-500)',
  },
  eGFR: {
    validate: (val) => {
      if (!Number.isFinite(Number(val))) return 'Must be a number';
      if (isEgfrOutOfRange(val)) return EGFR_RANGE_MESSAGE;
      return null;
    },
    parse: (val) => String(Number(val)),
    tooltip: `mL/min/1.73m² (${EGFR_MIN}-${EGFR_MAX})`,
  },
  qrsDuration: {
    validate: (val) => { const n = Number(val); if (isNaN(n)) return 'Must be a number'; if (n < 50 || n > 300) return '50-300 ms'; return null; },
    parse: (val) => String(val).replace(/[^\d.]/g, ''),
    tooltip: 'ms (50-300)',
  },
  prInterval: {
    validate: (val) => { const n = Number(val); if (isNaN(n)) return 'Must be a number'; return null; },
    parse: (val) => String(val).replace(/[^\d.]/g, ''),
    tooltip: 'ms',
  },
  qtcInterval: {
    validate: (val) => { const n = Number(val); if (isNaN(n)) return 'Must be a number'; if (n < 200 || n > 700) return '200-700 ms'; return null; },
    parse: (val) => String(val).replace(/[^\d.]/g, ''),
    tooltip: 'ms (200-700)',
  },
  height: {
    validate: (val) => { const n = Number(String(val).replace(/\s*inches?/i, '')); if (isNaN(n)) return 'Must be a number'; if (n < 36 || n > 96) return '36-96 inches'; return null; },
    parse: (val) => String(val).replace(/\s*inches?/i, '').trim(),
    tooltip: 'inches (36-96)',
  },
  weight: {
    validate: (val) => { const n = Number(String(val).replace(/\s*lbs?/i, '')); if (isNaN(n)) return 'Must be a number'; if (n < 50 || n > 800) return '50-800 lbs'; return null; },
    parse: (val) => String(val).replace(/\s*lbs?/i, '').trim(),
    tooltip: 'lbs (50-800)',
  },
  whr: {
    validate: () => null, // auto-calculated, rarely hand-entered
    parse: (val) => String(val).replace(/[^\d.]/g, ''),
    tooltip: 'ratio (auto-calculated)',
  },
};

// Custom header component for auto-calculated/derived columns
const CalculatedHeader = (props) => (
  <Tooltip title="Auto-calculated field">
    <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
      <span>{props.displayName}</span>
      <CalculatorOutlined style={{ fontSize: 13, color: '#40E0D0' }} />
    </div>
  </Tooltip>
);

// Custom header component for columns that open a popup modal in edit mode
const PopupHeader = (props) => (
  <Tooltip title="Opens entry form in edit mode">
    <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
      <span>{props.displayName}</span>
      <FormOutlined style={{ fontSize: 11, color: '#1890ff' }} />
    </div>
  </Tooltip>
);

const SpreadsheetView = ({ data, loading, teamMembers, onRefresh, pagination }) => {
  const [isEditMode, setIsEditMode] = useState(false);
  const [changedRows, setChangedRows] = useState({});
  const [saving, setSaving] = useState(false);
  const [originalData, setOriginalData] = useState({});
  const [bpModalOpen, setBpModalOpen] = useState(false);
  const [bpModalRow, setBpModalRow] = useState(null);
  const [bpModalReadings, setBpModalReadings] = useState([]);
  const [bpModalSelectedReadingKey, setBpModalSelectedReadingKey] = useState('new');
  const [bpModalValues, setBpModalValues] = useState({ systolic: null, diastolic: null, heartRate: null });
  const [ekgModalOpen, setEkgModalOpen] = useState(false);
  const [ekgModalRow, setEkgModalRow] = useState(null);
  const [ekgModalValues, setEkgModalValues] = useState({ heartRhythm: null, qrsDuration: null, prInterval: null, qtcInterval: null, ekgInterpretation: '' });
  const [hiddenFields, setHiddenFields] = useState(() => {
    try {
      const stored = localStorage.getItem(COL_VISIBILITY_KEY);
      if (stored) return new Set(JSON.parse(stored));
    } catch (e) { /* ignore parse errors */ }
    return new Set();
  });
  const [colPickerOpen, setColPickerOpen] = useState(false);
  const hiddenFieldsRef = useRef(hiddenFields);
  const gridRef = useRef(null);
  const navigate = useNavigate();

  const normalizeBpReadings = useCallback((rawReadings = []) => {
    return (rawReadings || [])
      .map((reading, idx) => {
        const key = String(reading?.id || reading?._id || reading?.timestamp || `reading_${idx}`);
        return {
          key,
          id: reading?.id || reading?._id || key,
          systolicBP: reading?.systolicBP,
          diastolicBP: reading?.diastolicBP,
          heartRate: reading?.heartRate,
          timestamp: reading?.timestamp || new Date().toISOString(),
          readingNumber: reading?.readingNumber || idx + 1,
          position: reading?.position || 'sitting',
        };
      })
      .filter(r => r.systolicBP && r.diastolicBP)
      .sort((a, b) => new Date(b.timestamp || 0) - new Date(a.timestamp || 0));
  }, []);

  const mapBpReadingsForDisplay = useCallback((rawReadings = []) => {
    return normalizeBpReadings(rawReadings).map((reading) => ({
      bp: `${reading.systolicBP}/${reading.diastolicBP}`,
      heartRate: reading.heartRate || '',
      timeLabel: '',
      timestamp: reading.timestamp,
    }));
  }, [normalizeBpReadings]);

  // Apply column visibility via AG Grid API (stable, reads from ref)
  const applyColumnVisibility = useCallback(() => {
    if (!gridRef.current?.api) return;
    const api = gridRef.current.api;
    const allFields = COLUMN_GROUPS.flatMap(g => g.columns.map(c => c.field));
    const toHide = allFields.filter(f => hiddenFieldsRef.current.has(f));
    const toShow = allFields.filter(f => !hiddenFieldsRef.current.has(f));
    if (toHide.length) api.setColumnsVisible(toHide, false);
    if (toShow.length) api.setColumnsVisible(toShow, true);
  }, []);

  // Sync ref, apply visibility, persist to localStorage whenever hiddenFields changes
  useEffect(() => {
    hiddenFieldsRef.current = hiddenFields;
    applyColumnVisibility();
    try {
      localStorage.setItem(COL_VISIBILITY_KEY, JSON.stringify([...hiddenFields]));
    } catch (e) { /* ignore storage errors */ }
  }, [hiddenFields, applyColumnVisibility]);

  // Toggle a single column field
  const toggleField = useCallback((field) => {
    setHiddenFields(prev => {
      const next = new Set(prev);
      if (next.has(field)) next.delete(field);
      else next.add(field);
      return next;
    });
  }, []);

  // Toggle all columns in a group
  const toggleGroup = useCallback((groupFields) => {
    setHiddenFields(prev => {
      const next = new Set(prev);
      const allHidden = groupFields.every(f => next.has(f));
      if (allHidden) groupFields.forEach(f => next.delete(f));
      else groupFields.forEach(f => next.add(f));
      return next;
    });
  }, []);

  // Store original data snapshot when entering edit mode
  const handleToggleEditMode = useCallback((checked) => {
    if (checked) {
      // Snapshot current data
      const snapshot = {};
      data.forEach(row => { snapshot[row.id] = { ...row }; });
      setOriginalData(snapshot);
      setChangedRows({});
    } else {
      // Exiting edit mode, check for unsaved changes
      if (Object.keys(changedRows).length > 0) {
        if (!window.confirm('You have unsaved changes. Discard them?')) return;
      }
      setChangedRows({});
      setOriginalData({});
    }
    setIsEditMode(checked);
  }, [data, changedRows]);

  // Track cell edits
  const onCellValueChanged = useCallback((event) => {
    const rowId = event.data.id;
    const field = event.colDef.field;
    const oldVal = originalData[rowId]?.[field];
    const newVal = event.newValue;

    setChangedRows(prev => {
      const existing = prev[rowId] || {};
      // If value reverted to original, remove field
      if (oldVal === newVal) {
        const { [field]: _removed, ...rest } = existing; // eslint-disable-line no-unused-vars
        if (Object.keys(rest).length === 0) {
          const { [rowId]: _removedRow, ...remainingRows } = prev; // eslint-disable-line no-unused-vars
          return remainingRows;
        }
        return { ...prev, [rowId]: rest };
      }
      return {
        ...prev,
        [rowId]: { ...existing, [field]: { old: oldVal, new: newVal } }
      };
    });
  }, [originalData]);

  // Batch save all changes
  const handleSaveAll = useCallback(async () => {
    const changeEntries = Object.entries(changedRows);
    if (changeEntries.length === 0) return;

    setSaving(true);
    let succeeded = 0;
    let failed = 0;

    for (const [rowId, fields] of changeEntries) {
      try {
        const row = data.find(r => r.id === rowId);
        if (!row) continue;

        // Separate visit-level updates from patient-level updates
        const patientUpdates = {};
        const screeningUpdates = {};
        let visitStatusUpdate = null;

        // Fields that map to screening data
        const SCREENING_FIELDS = {
          bloodPressure: (val) => {
            const parts = String(val).split('/');
            if (parts.length === 2) {
              return { systolicBP: Number(parts[0].trim()), diastolicBP: Number(parts[1].trim()) };
            }
            return {};
          },
          heartRate: (val) => ({ heartRate: Number(String(val).replace(/\s*bpm/i, '')), pulseRate: Number(String(val).replace(/\s*bpm/i, '')) }),
          totalCholesterol: (val) => ({ totalCholesterol: Number(String(val).replace(/[^\d.]/g, '')) }),
          hdlCholesterol: (val) => ({ hdlCholesterol: Number(String(val).replace(/[^\d.]/g, '')) }),
          ldlCholesterol: (val) => ({ ldlCholesterol: Number(String(val).replace(/[^\d.]/g, '')) }),
          triglycerides: (val) => ({ triglycerides: Number(String(val).replace(/[^\d.]/g, '')) }),
          bloodGlucose: (val) => ({ bloodGlucose: Number(String(val).replace(/[^\d.]/g, '')) }),
          a1c: (val) => ({ a1c: Number(String(val).replace(/[^\d.]/g, '')) }),
          lipoproteinA: (val) => ({ lipoproteinA: Number(String(val).replace(/[^\d.]/g, '')) }),
          eGFR: (val) => ({ eGFR: val === '' || val === null ? null : Number(val) }),
          fastingStatus: (val) => ({ fastingStatus: val === 'Fasting' }),
          heartRhythm: (val) => ({ heartRhythm: val }), // stored as value key (e.g. 'normal_sinus')
          qrsDuration: (val) => ({ qrsDuration: Number(String(val).replace(/[^\d.]/g, '')) }),
          prInterval: (val) => ({ prInterval: Number(String(val).replace(/[^\d.]/g, '')) }),
          qtcInterval: (val) => ({ qtcInterval: Number(String(val).replace(/[^\d.]/g, '')) }),
          ekgInterpretation: (val) => ({ ekgInterpretation: val }),
          height: (val) => ({ height: Number(String(val).replace(/\s*inches?/i, '').replace(/[^\d.]/g, '')) }),
          weight: (val) => ({ weight: Number(String(val).replace(/\s*lbs?/i, '').replace(/[^\d.]/g, '')) }),
        };

        for (const [field, change] of Object.entries(fields)) {
          if (SCREENING_FIELDS[field]) {
            Object.assign(screeningUpdates, SCREENING_FIELDS[field](change.new));
          } else {
            switch (field) {
              case 'patientName': {
                const parts = change.new.trim().split(/\s+/);
                patientUpdates.firstName = parts[0] || '';
                patientUpdates.lastName = parts.slice(1).join(' ') || '';
                break;
              }
              case 'phone':
                patientUpdates.phoneNumber = change.new;
                break;
              case 'gender':
                patientUpdates.birthGender = change.new;
                break;
              case 'status':
                visitStatusUpdate = change.new;
                break;
              case 'bloodTestHandlerName': {
                // Find team member ID by name
                const member = teamMembers.find(m =>
                  (m.fullName || m.email) === change.new
                );
                if (member) patientUpdates.bloodTestHandler = member._id;
                break;
              }
              case 'testingCode':
                patientUpdates.testingCode = change.new || '';
                break;
              case 'bpReadingsRaw':
                // handled below when processing screening updates
                break;
              default:
                break;
            }
          }
        }

        // If the BP readings array changed, persist it into the latest visit's vitalSigns
        const bpReadingsChange = fields.bpReadingsRaw?.new;
        if (Array.isArray(bpReadingsChange) && row.fullPatientData?.screeningData) {
          const existingScreeningData = row.fullPatientData.screeningData || {};
          const existingMedicalReadings = Array.isArray(existingScreeningData.medicalReadings)
            ? [...existingScreeningData.medicalReadings]
            : [];

          if (existingMedicalReadings.length > 0) {
            const latestIndex = existingMedicalReadings
              .map((visit, idx) => ({ idx, visitDate: new Date(visit?.visitDate || 0).getTime() }))
              .sort((a, b) => b.visitDate - a.visitDate)[0]?.idx;

            if (latestIndex !== undefined) {
              const latestVisit = { ...existingMedicalReadings[latestIndex] };
              latestVisit.vitalSigns = bpReadingsChange.map((reading, idx) => ({
                id: reading.id,
                systolicBP: Number(reading.systolicBP),
                diastolicBP: Number(reading.diastolicBP),
                heartRate: Number(reading.heartRate),
                timestamp: reading.timestamp,
                readingNumber: reading.readingNumber || idx + 1,
                position: reading.position || 'sitting',
              }));
              existingMedicalReadings[latestIndex] = latestVisit;

              screeningUpdates.medicalReadings = existingMedicalReadings;

              // Keep legacy flat fields aligned with latest reading for compatibility.
              const newest = latestVisit.vitalSigns
                .slice()
                .sort((a, b) => new Date(b.timestamp || 0) - new Date(a.timestamp || 0))[0];
              if (newest) {
                screeningUpdates.systolicBP = Number(newest.systolicBP);
                screeningUpdates.diastolicBP = Number(newest.diastolicBP);
                screeningUpdates.heartRate = Number(newest.heartRate);
                screeningUpdates.pulseRate = Number(newest.heartRate);
              }
            }
          }
        }

        // Merge patient-level and screening-level updates into a single call
        // matching the edit page pattern: PUT /screening/patient/{patientId}
        const combinedUpdates = { ...patientUpdates };
        if (Object.keys(screeningUpdates).length > 0) {
          // Merge with existing screeningData to avoid overwriting unrelated fields
          const existingScreeningData = row.fullPatientData?.screeningData || {};
          combinedUpdates.screeningData = {
            ...existingScreeningData,
            ...screeningUpdates,
            lastUpdated: new Date().toISOString()
          };
          if (fields.eGFR) {
            combinedUpdates.screeningData = updateSpreadsheetEgfr(combinedUpdates.screeningData, fields.eGFR.new);
          }
        }

        // Apply combined patient + screening updates
        if (Object.keys(combinedUpdates).length > 0) {
          await PatientService.updatePatient(row.patientId, combinedUpdates);
        }

        // Apply visit status update
        if (visitStatusUpdate && row.id) {
          await PatientService.updateQueueStatus(row.id, { status: visitStatusUpdate });
        }

        succeeded++;
      } catch (err) {
        console.error(`Failed to save row ${rowId}:`, err);
        failed++;
      }
    }

    setSaving(false);
    if (failed > 0) {
      message.warning(`Saved ${succeeded} records, ${failed} failed`);
    } else {
      message.success(`Saved ${succeeded} records`);
    }
    setChangedRows({});
    // Stay in edit mode — re-snapshot current data as new baseline
    if (onRefresh) {
      await onRefresh();
    }
    // Re-snapshot after refresh so further edits track from the saved state
    setTimeout(() => {
      if (gridRef.current?.api) {
        const snapshot = {};
        gridRef.current.api.forEachNode(node => {
          if (node.data) snapshot[node.data.id] = { ...node.data };
        });
        setOriginalData(snapshot);
      }
    }, 500);
  }, [changedRows, data, teamMembers, onRefresh]);

  // Discard all changes
  const handleDiscard = useCallback(() => {
    if (Object.keys(changedRows).length > 0) {
      // Revert grid data from original snapshot
      if (gridRef.current?.api) {
        const api = gridRef.current.api;
        Object.entries(originalData).forEach(([rowId, origRow]) => {
          const rowNode = api.getRowNode(rowId);
          if (rowNode) rowNode.setData(origRow);
        });
      }
    }
    setChangedRows({});
    setOriginalData({});
    setIsEditMode(false);
  }, [changedRows, originalData]);

  // Warn before leaving with unsaved changes
  useEffect(() => {
    const handler = (e) => {
      if (Object.keys(changedRows).length > 0) {
        e.preventDefault();
        e.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [changedRows]);

  // Open BP modal for a row
  const openBpModal = useCallback((rowData) => {
    const normalized = normalizeBpReadings(rowData.bpReadingsRaw || []);
    const selectedKey = normalized.length > 0 ? normalized[0].key : 'new';
    const selectedReading = normalized.find(r => r.key === selectedKey);

    setBpModalRow(rowData);
    setBpModalReadings(normalized);
    setBpModalSelectedReadingKey(selectedKey);
    setBpModalValues({
      systolic: selectedReading?.systolicBP || null,
      diastolic: selectedReading?.diastolicBP || null,
      heartRate: selectedReading?.heartRate || null,
    });
    setBpModalOpen(true);
  }, [normalizeBpReadings]);

  // Save BP modal values back to the grid
  const handleBpModalSave = useCallback(() => {
    if (!bpModalRow) return;
    const { systolic, diastolic, heartRate } = bpModalValues;
    // Validate
    if (systolic !== null && (systolic < 50 || systolic > 300)) {
      message.error('Systolic must be 50-300 mmHg');
      return;
    }
    if (diastolic !== null && (diastolic < 30 || diastolic > 200)) {
      message.error('Diastolic must be 30-200 mmHg');
      return;
    }
    if (heartRate !== null && (heartRate < 30 || heartRate > 250)) {
      message.error('Heart Rate must be 30-250 bpm');
      return;
    }

    const rowId = bpModalRow.id;
    const api = gridRef.current?.api;
    const rowNode = api?.getRowNode(rowId);
    if (!rowNode) return;

    if (!(systolic && diastolic && heartRate)) {
      message.error('Please enter Systolic, Diastolic, and Heart Rate');
      return;
    }

    const updatedReadings = [...bpModalReadings];
    if (bpModalSelectedReadingKey === 'new') {
      updatedReadings.push({
        key: `reading_${Date.now()}`,
        id: `reading_${Date.now()}`,
        systolicBP: Number(systolic),
        diastolicBP: Number(diastolic),
        heartRate: Number(heartRate),
        timestamp: new Date().toISOString(),
        readingNumber: (updatedReadings.length || 0) + 1,
        position: 'sitting',
      });
    } else {
      const idx = updatedReadings.findIndex(r => r.key === bpModalSelectedReadingKey);
      if (idx >= 0) {
        updatedReadings[idx] = {
          ...updatedReadings[idx],
          systolicBP: Number(systolic),
          diastolicBP: Number(diastolic),
          heartRate: Number(heartRate),
        };
      }
    }

    const sortedReadings = normalizeBpReadings(updatedReadings);
    const latestReading = sortedReadings[0];

    const newBp = latestReading ? `${latestReading.systolicBP}/${latestReading.diastolicBP}` : '';
    const newHr = latestReading?.heartRate ? String(latestReading.heartRate) : '';
    const oldBp = originalData[rowId]?.bloodPressure;
    const oldHr = originalData[rowId]?.heartRate;

    rowNode.setDataValue('bloodPressure', newBp || 'Not recorded');
    rowNode.setDataValue('heartRate', newHr || 'Not recorded');
    rowNode.setDataValue('bpReadingsRaw', sortedReadings);
    rowNode.setDataValue('bpReadingsHistory', mapBpReadingsForDisplay(sortedReadings));

    // Track changes
    setChangedRows(prev => {
      const existing = prev[rowId] || {};
      const updated = { ...existing };

      if (newBp !== oldBp && newBp) {
        updated.bloodPressure = { old: oldBp, new: newBp };
      } else {
        delete updated.bloodPressure;
      }

      if (newHr !== String(oldHr) && newHr) {
        updated.heartRate = { old: oldHr, new: newHr };
      } else {
        delete updated.heartRate;
      }

      updated.bpReadingsRaw = {
        old: originalData[rowId]?.bpReadingsRaw || [],
        new: sortedReadings,
      };

      if (Object.keys(updated).length === 0) {
        const { [rowId]: _removed, ...rest } = prev; // eslint-disable-line no-unused-vars
        return rest;
      }
      return { ...prev, [rowId]: updated };
    });

    setBpModalOpen(false);
    setBpModalRow(null);
    setBpModalReadings([]);
    setBpModalSelectedReadingKey('new');
  }, [bpModalRow, bpModalValues, bpModalReadings, bpModalSelectedReadingKey, originalData, normalizeBpReadings, mapBpReadingsForDisplay]);

  // Open EKG modal for a row
  const openEkgModal = useCallback((rowData) => {
    const hr = !isMissing(rowData.heartRhythm) ? rowData.heartRhythm : null;
    const qrs = !isMissing(rowData.qrsDuration) ? Number(String(rowData.qrsDuration).replace(/[^\d.]/g, '')) || null : null;
    const pr = !isMissing(rowData.prInterval) ? Number(String(rowData.prInterval).replace(/[^\d.]/g, '')) || null : null;
    const qtc = !isMissing(rowData.qtcInterval) ? Number(String(rowData.qtcInterval).replace(/[^\d.]/g, '')) || null : null;
    const interp = !isMissing(rowData.ekgInterpretation) ? rowData.ekgInterpretation : '';
    setEkgModalRow(rowData);
    setEkgModalValues({ heartRhythm: hr, qrsDuration: qrs, prInterval: pr, qtcInterval: qtc, ekgInterpretation: interp });
    setEkgModalOpen(true);
  }, []);

  // Save EKG modal values back to the grid
  const handleEkgModalSave = useCallback(() => {
    if (!ekgModalRow) return;
    const { heartRhythm, qrsDuration, prInterval, qtcInterval, ekgInterpretation } = ekgModalValues;
    if (qrsDuration !== null && (qrsDuration < 50 || qrsDuration > 300)) { message.error('QRS must be 50-300 ms'); return; }
    if (qtcInterval !== null && (qtcInterval < 200 || qtcInterval > 700)) { message.error('QTc must be 200-700 ms'); return; }

    const rowId = ekgModalRow.id;
    const api = gridRef.current?.api;
    const rowNode = api?.getRowNode(rowId);
    if (!rowNode) return;

    const fields = {
      heartRhythm: heartRhythm || 'Not performed',
      qrsDuration: qrsDuration ? String(qrsDuration) : 'Not measured',
      prInterval: prInterval ? String(prInterval) : 'Not measured',
      qtcInterval: qtcInterval ? String(qtcInterval) : 'Not measured',
      ekgInterpretation: ekgInterpretation || 'Not provided',
    };

    Object.entries(fields).forEach(([f, v]) => rowNode.setDataValue(f, v));

    setChangedRows(prev => {
      const existing = prev[rowId] || {};
      const updated = { ...existing };
      Object.entries(fields).forEach(([f, v]) => {
        const orig = originalData[rowId]?.[f];
        if (String(v) !== String(orig) && !isMissing(v)) {
          updated[f] = { old: orig, new: v };
        } else {
          delete updated[f];
        }
      });
      if (Object.keys(updated).length === 0) {
        const { [rowId]: _removed, ...rest } = prev; // eslint-disable-line no-unused-vars
        return rest;
      }
      return { ...prev, [rowId]: updated };
    });

    setEkgModalOpen(false);
    setEkgModalRow(null);
  }, [ekgModalRow, ekgModalValues, originalData]);

  // Value formatter for displaying missing data
  const missingValueFormatter = (params) => {
    if (isMissing(params.value)) return '';
    return params.value;
  };

  // Rhythm formatter: show human label instead of value key
  const rhythmFormatter = (params) => {
    if (isMissing(params.value)) return '';
    return RHYTHM_LABELS[params.value] || params.value;
  };

  // Validated value setter: validates input and shows error if invalid
  const makeValidatedSetter = (field) => (params) => {
    const rule = FIELD_VALIDATION[field];
    if (!rule) {
      params.data[field] = params.newValue;
      return true;
    }
    const raw = params.newValue;
    // Allow clearing a field
    if (raw === '' || raw === null || raw === undefined) {
      params.data[field] = '';
      return true;
    }
    const error = rule.validate(raw);
    if (error) {
      message.error(`${field}: ${error}`);
      return false; // reject edit
    }
    params.data[field] = rule.parse ? rule.parse(raw) : raw;
    return true;
  };

  // Column definitions with grouped headers
  const columnDefs = useMemo(() => {
    const teamMemberNames = teamMembers.map(m => m.fullName || m.email);

    const renderBpCell = (params) => {
      const displayValue = isMissing(params.value) ? '' : params.value;
      const readings = Array.isArray(params.data?.bpReadingsHistory) ? params.data.bpReadingsHistory : [];
      if (!displayValue || readings.length <= 1) return displayValue;

      return (
        <Tooltip
          title={
            <div style={{ minWidth: 180 }}>
              {readings.map((reading, index) => (
                <div
                  key={`${reading.timestamp || index}_${index}`}
                  style={{
                    whiteSpace: 'nowrap',
                    padding: '4px 0',
                    borderBottom: index < readings.length - 1 ? '1px solid rgba(255,255,255,0.16)' : 'none'
                  }}
                >
                  {reading.bp}{reading.heartRate ? ` • HR ${reading.heartRate}` : ''}
                </div>
              ))}
            </div>
          }
          placement="top"
        >
          <span style={{ textDecoration: 'underline dotted', textUnderlineOffset: 2 }}>
            {displayValue}
          </span>
        </Tooltip>
      );
    };

    return [
      // Pinned: Member Info
      {
        headerName: 'Member Info',
        children: [
          {
            headerName: 'Name',
            field: 'patientName',
            pinned: 'left',
            minWidth: 180,
            flex: 1,
            editable: isEditMode,
            cellClassRules: {
              'cell-changed': (params) => !!changedRows[params.data?.id]?.patientName,
            },
          },
          {
            headerName: 'Phone',
            field: 'phone',
            pinned: 'left',
            minWidth: 145,
            editable: isEditMode,
            cellClassRules: {
              'cell-changed': (params) => !!changedRows[params.data?.id]?.phone,
            },
            valueFormatter: missingValueFormatter,
          },
          {
            headerName: 'Age',
            field: 'age',
            pinned: 'left',
            minWidth: 65,
            editable: false,
            headerComponent: CalculatedHeader,
            valueFormatter: (params) => params.value || 'N/A',
          },
          {
            headerName: 'Gender',
            field: 'gender',
            pinned: 'left',
            minWidth: 110,
            editable: isEditMode,
            cellEditor: 'agSelectCellEditor',
            cellEditorParams: { values: GENDER_OPTIONS },
            cellClassRules: {
              'cell-changed': (params) => !!changedRows[params.data?.id]?.gender,
            },
            valueFormatter: missingValueFormatter,
          },
          {
            headerName: 'Testing Code',
            field: 'testingCode',
            pinned: 'left',
            minWidth: 130,
            editable: isEditMode,
            headerTooltip: 'Screening testing code',
            valueFormatter: missingValueFormatter,
            cellClassRules: {
              'cell-changed': (params) => !!changedRows[params.data?.id]?.testingCode,
            },
          },
        ],
      },
      // Vital Signs
      {
        headerName: 'Vital Signs',
        children: [
          {
            headerName: 'BP',
            field: 'bloodPressure',
            minWidth: 100,
            editable: false,
            headerComponent: PopupHeader,
            headerTooltip: 'Click cell in edit mode to enter Systolic/Diastolic/HR',
            onCellClicked: (params) => { if (isEditMode) openBpModal(params.data); },
            cellStyle: () => isEditMode ? { cursor: 'pointer' } : {},
            valueFormatter: missingValueFormatter,
            cellRenderer: renderBpCell,
            cellClassRules: { ...makeCellClassRules('bloodPressure'), 'cell-changed': (params) => !!changedRows[params.data?.id]?.bloodPressure },
          },
          {
            headerName: 'HR',
            field: 'heartRate',
            minWidth: 70,
            editable: false,
            headerComponent: PopupHeader,
            headerTooltip: 'Click BP cell in edit mode to enter Heart Rate',
            onCellClicked: (params) => { if (isEditMode) openBpModal(params.data); },
            cellStyle: () => isEditMode ? { cursor: 'pointer' } : {},
            valueFormatter: missingValueFormatter,
            cellClassRules: { ...makeCellClassRules('heartRate'), 'cell-changed': (params) => !!changedRows[params.data?.id]?.heartRate },
          },
        ],
      },
      // Blood Work
      {
        headerName: 'Blood Work',
        children: [
          {
            headerName: 'Chol',
            field: 'totalCholesterol',
            minWidth: 75,
            editable: isEditMode,
            headerTooltip: 'Total Cholesterol mg/dL (50-500)',
            valueSetter: makeValidatedSetter('totalCholesterol'),
            valueFormatter: missingValueFormatter,
            cellClassRules: { ...makeCellClassRules('totalCholesterol'), 'cell-changed': (params) => !!changedRows[params.data?.id]?.totalCholesterol },
          },
          {
            headerName: 'HDL',
            field: 'hdlCholesterol',
            minWidth: 70,
            editable: isEditMode,
            headerTooltip: 'HDL Cholesterol mg/dL (10-200)',
            valueSetter: makeValidatedSetter('hdlCholesterol'),
            valueFormatter: missingValueFormatter,
            cellClassRules: { ...makeCellClassRules('hdlCholesterol'), 'cell-changed': (params) => !!changedRows[params.data?.id]?.hdlCholesterol },
          },
          {
            headerName: 'LDL',
            field: 'ldlCholesterol',
            minWidth: 70,
            editable: isEditMode,
            headerTooltip: 'LDL Cholesterol mg/dL (10-400)',
            valueSetter: makeValidatedSetter('ldlCholesterol'),
            valueFormatter: missingValueFormatter,
            cellClassRules: { ...makeCellClassRules('ldlCholesterol'), 'cell-changed': (params) => !!changedRows[params.data?.id]?.ldlCholesterol },
          },
          {
            headerName: 'Trig',
            field: 'triglycerides',
            minWidth: 70,
            editable: isEditMode,
            headerTooltip: 'Triglycerides mg/dL (10-1000)',
            valueSetter: makeValidatedSetter('triglycerides'),
            valueFormatter: missingValueFormatter,
            cellClassRules: { ...makeCellClassRules('triglycerides'), 'cell-changed': (params) => !!changedRows[params.data?.id]?.triglycerides },
          },
          {
            headerName: 'eGFR',
            field: 'eGFR',
            minWidth: 85,
            editable: isEditMode,
            headerTooltip: `Estimated Glomerular Filtration Rate ${FIELD_VALIDATION.eGFR.tooltip}`,
            valueSetter: makeValidatedSetter('eGFR'),
            valueFormatter: missingValueFormatter,
            cellClassRules: { ...makeCellClassRules('eGFR'), 'cell-changed': (params) => !!changedRows[params.data?.id]?.eGFR },
          },
          {
            headerName: 'Glucose',
            field: 'bloodGlucose',
            minWidth: 85,
            editable: isEditMode,
            headerTooltip: 'Blood Glucose mg/dL (30-500)',
            valueSetter: makeValidatedSetter('bloodGlucose'),
            valueFormatter: missingValueFormatter,
            cellClassRules: { ...makeCellClassRules('bloodGlucose'), 'cell-changed': (params) => !!changedRows[params.data?.id]?.bloodGlucose },
          },
          {
            headerName: 'A1C',
            field: 'a1c',
            minWidth: 65,
            editable: isEditMode,
            headerTooltip: 'Hemoglobin A1C % (3-20)',
            valueSetter: makeValidatedSetter('a1c'),
            valueFormatter: missingValueFormatter,
            cellClassRules: { ...makeCellClassRules('a1c'), 'cell-changed': (params) => !!changedRows[params.data?.id]?.a1c },
          },
          {
            headerName: 'Lp(a)',
            field: 'lipoproteinA',
            minWidth: 70,
            editable: isEditMode,
            headerTooltip: 'Lipoprotein(a) nmol/L (0-500)',
            valueSetter: makeValidatedSetter('lipoproteinA'),
            valueFormatter: missingValueFormatter,
            cellClassRules: { ...makeCellClassRules('lipoproteinA'), 'cell-changed': (params) => !!changedRows[params.data?.id]?.lipoproteinA },
          },
          {
            headerName: 'Fasting',
            field: 'fastingStatus',
            minWidth: 90,
            editable: isEditMode,
            cellEditor: 'agSelectCellEditor',
            cellEditorParams: { values: ['Fasting', 'Non-fasting'] },
            valueFormatter: missingValueFormatter,
            cellClassRules: { 'cell-changed': (params) => !!changedRows[params.data?.id]?.fastingStatus },
          },
        ],
      },
      // EKG
      {
        headerName: 'EKG',
        children: [
          {
            headerName: 'Rhythm',
            field: 'heartRhythm',
            minWidth: 140,
            editable: false,
            headerComponent: PopupHeader,
            headerTooltip: 'Click cell in edit mode to enter EKG data',
            onCellClicked: (params) => { if (isEditMode) openEkgModal(params.data); },
            cellStyle: () => isEditMode ? { cursor: 'pointer' } : {},
            valueFormatter: rhythmFormatter,
            cellClassRules: { ...makeCellClassRules('heartRhythm'), 'cell-changed': (params) => !!changedRows[params.data?.id]?.heartRhythm },
          },
          {
            headerName: 'QRS',
            field: 'qrsDuration',
            minWidth: 70,
            editable: false,
            headerComponent: PopupHeader,
            headerTooltip: 'Click to enter EKG data',
            onCellClicked: (params) => { if (isEditMode) openEkgModal(params.data); },
            cellStyle: () => isEditMode ? { cursor: 'pointer' } : {},
            valueFormatter: missingValueFormatter,
            cellClassRules: { 'cell-changed': (params) => !!changedRows[params.data?.id]?.qrsDuration },
          },
          {
            headerName: 'PR',
            field: 'prInterval',
            minWidth: 65,
            editable: false,
            headerComponent: PopupHeader,
            headerTooltip: 'Click to enter EKG data',
            onCellClicked: (params) => { if (isEditMode) openEkgModal(params.data); },
            cellStyle: () => isEditMode ? { cursor: 'pointer' } : {},
            valueFormatter: missingValueFormatter,
            cellClassRules: { 'cell-changed': (params) => !!changedRows[params.data?.id]?.prInterval },
          },
          {
            headerName: 'QTc',
            field: 'qtcInterval',
            minWidth: 70,
            editable: false,
            headerComponent: PopupHeader,
            headerTooltip: 'Click to enter EKG data',
            onCellClicked: (params) => { if (isEditMode) openEkgModal(params.data); },
            cellStyle: () => isEditMode ? { cursor: 'pointer' } : {},
            valueFormatter: missingValueFormatter,
            cellClassRules: { 'cell-changed': (params) => !!changedRows[params.data?.id]?.qtcInterval },
          },
          {
            headerName: 'Interp.',
            field: 'ekgInterpretation',
            minWidth: 130,
            editable: false,
            headerComponent: PopupHeader,
            headerTooltip: 'Click to enter EKG data',
            onCellClicked: (params) => { if (isEditMode) openEkgModal(params.data); },
            cellStyle: () => isEditMode ? { cursor: 'pointer' } : {},
            valueFormatter: missingValueFormatter,
            cellClassRules: { ...makeCellClassRules('ekgInterpretation'), 'cell-changed': (params) => !!changedRows[params.data?.id]?.ekgInterpretation },
          },
        ],
      },
      // Body Measurements
      {
        headerName: 'Body',
        children: [
          {
            headerName: 'Height',
            field: 'height',
            minWidth: 90,
            editable: isEditMode,
            headerTooltip: 'Height in inches (36-96)',
            valueSetter: makeValidatedSetter('height'),
            valueFormatter: missingValueFormatter,
            cellClassRules: { ...makeCellClassRules('height'), 'cell-changed': (params) => !!changedRows[params.data?.id]?.height },
          },
          {
            headerName: 'Weight',
            field: 'weight',
            minWidth: 95,
            editable: isEditMode,
            headerTooltip: 'Weight in lbs (50-800)',
            valueSetter: makeValidatedSetter('weight'),
            valueFormatter: missingValueFormatter,
            cellClassRules: { ...makeCellClassRules('weight'), 'cell-changed': (params) => !!changedRows[params.data?.id]?.weight },
          },
          {
            headerName: 'WHR',
            field: 'whr',
            minWidth: 75,
            editable: false,
            headerComponent: CalculatedHeader,
            headerTooltip: 'Waist-to-Hip Ratio (auto-calculated)',
            valueFormatter: missingValueFormatter,
            cellClassRules: { ...makeCellClassRules('whr') },
          },
        ],
      },
      // Status & Assignment
      {
        headerName: 'Status',
        children: [
          {
            headerName: 'Risk',
            field: 'riskLevel',
            minWidth: 95,
            editable: false,
            headerComponent: CalculatedHeader,
            cellClassRules: {
              'risk-critical': (params) => params.value === 'critical',
              'risk-high': (params) => params.value === 'high',
              'risk-medium': (params) => params.value === 'medium',
              'risk-low': (params) => params.value === 'low',
              'risk-minimal': (params) => params.value === 'minimal',
            },
            valueFormatter: (params) => {
              if (!params.value || params.value === 'unknown') return 'No Data';
              return params.value.charAt(0).toUpperCase() + params.value.slice(1);
            },
          },
          {
            headerName: 'Status',
            field: 'status',
            minWidth: 130,
            editable: isEditMode,
            cellEditor: 'agSelectCellEditor',
            cellEditorParams: { values: STATUS_OPTIONS },
            cellClassRules: {
              'status-completed': (params) => params.value === 'completed',
              'status-in_progress': (params) => params.value === 'in_progress',
              'status-pending_review': (params) => params.value === 'pending_review',
              'status-archived': (params) => params.value === 'archived',
              'status-new_patient': (params) => params.value === 'new_patient',
              'cell-changed': (params) => !!changedRows[params.data?.id]?.status,
            },
            valueFormatter: (params) => {
              if (!params.value) return '';
              return params.value.replace(/_/g, ' ');
            },
          },
          {
            headerName: 'Event',
            field: 'eventTitle',
            minWidth: 170,
            editable: false,
            valueFormatter: missingValueFormatter,
          },
          {
            headerName: 'Handler',
            field: 'bloodTestHandlerName',
            minWidth: 130,
            editable: isEditMode,
            cellEditor: 'agSelectCellEditor',
            cellEditorParams: { values: teamMemberNames },
            cellClassRules: {
              'cell-changed': (params) => !!changedRows[params.data?.id]?.bloodTestHandlerName,
            },
            valueFormatter: missingValueFormatter,
          },
        ],
      },
    ];
  }, [isEditMode, changedRows, teamMembers]);

  // Default column settings
  const defaultColDef = useMemo(() => ({
    resizable: true,
    sortable: true,
    filter: false,
    suppressMovable: false,
  }), []);

  // Row click handler - navigate to record (only in view mode)
  const onRowClicked = useCallback((event) => {
    if (isEditMode) return;
    if (!event.data?.patientId) return;
    navigate(ROUTES.ADMIN_MEMBER_RECORD_EDIT.replace(':patientId', event.data.patientId));
  }, [isEditMode, navigate]);

  // Grid ready handler
  const onGridReady = useCallback(() => {
    applyColumnVisibility();
  }, [applyColumnVisibility]);

  const changeCount = Object.keys(changedRows).length;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Toolbar */}
      <div className="spreadsheet-toolbar">
        <div className="edit-toggle">
          <Switch
            checked={isEditMode}
            onChange={handleToggleEditMode}
            checkedChildren={<EditOutlined />}
            unCheckedChildren={<EditOutlined />}
            style={{ backgroundColor: isEditMode ? '#1890ff' : undefined }}
          />
          <span style={{ fontSize: 13, fontWeight: 500, color: isEditMode ? '#1890ff' : '#666' }}>
            {isEditMode ? 'Edit Mode ON' : 'Edit Mode'}
          </span>
          {isEditMode && (
            <Tooltip title="Click any highlighted cell to edit. Tab to move between cells.">
              <InfoCircleOutlined style={{ color: '#1890ff', fontSize: 14 }} />
            </Tooltip>
          )}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          <Popover
            open={colPickerOpen}
            onOpenChange={setColPickerOpen}
            trigger="click"
            placement="bottomRight"
            title={
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span>Show / Hide Columns</span>
                <button
                  onClick={() => setHiddenFields(new Set())}
                  style={{ fontSize: 12, color: '#1890ff', border: 'none', background: 'none', cursor: 'pointer', padding: 0 }}
                >
                  Show All
                </button>
              </div>
            }
            content={
              <div style={{ width: 230, maxHeight: 440, overflowY: 'auto', padding: '4px 0' }}>
                {COLUMN_GROUPS.map(({ group, columns }) => {
                  const groupFields = columns.map(c => c.field);
                  const allVisible = groupFields.every(f => !hiddenFields.has(f));
                  const someVisible = groupFields.some(f => !hiddenFields.has(f));
                  return (
                    <div key={group} style={{ marginBottom: 10 }}>
                      <Checkbox
                        checked={allVisible}
                        indeterminate={!allVisible && someVisible}
                        onChange={() => toggleGroup(groupFields)}
                        style={{ fontWeight: 700, fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.4px', color: '#333' }}
                      >
                        {group}
                      </Checkbox>
                      <div style={{ paddingLeft: 22, marginTop: 4, display: 'flex', flexDirection: 'column', gap: 3 }}>
                        {columns.map(({ field, label }) => (
                          <Checkbox
                            key={field}
                            checked={!hiddenFields.has(field)}
                            onChange={() => toggleField(field)}
                            style={{ fontSize: 13, color: '#555' }}
                          >
                            {label}
                          </Checkbox>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            }
          >
            <button className="btn-columns">
              <EyeOutlined style={{ fontSize: 13 }} />
              Columns {hiddenFields.size > 0 ? `(${hiddenFields.size} hidden)` : ''}
            </button>
          </Popover>
          <div className="legend">
            <div className="legend-item">
              <div className="legend-dot critical" />
              <span>Missing (critical)</span>
            </div>
            <div className="legend-item">
              <div className="legend-dot recommended" />
              <span>Missing (recommended)</span>
            </div>
            <div className="legend-item">
              <div className="legend-dot filled" />
              <span>Has data</span>
            </div>
          </div>
        </div>
      </div>

      {/* AG Grid */}
      <div
        className={`bha-spreadsheet ag-theme-alpine ${isEditMode ? 'edit-mode' : ''}`}
        style={{ flex: 1, minHeight: 400 }}
      >
        <AgGridReact
          ref={gridRef}
          rowData={data}
          columnDefs={columnDefs}
          defaultColDef={defaultColDef}
          getRowId={(params) => params.data.id}
          rowSelection="multiple"
          suppressRowClickSelection={true}
          onRowClicked={onRowClicked}
          onCellValueChanged={onCellValueChanged}
          onGridReady={onGridReady}
          animateRows={true}
          enableCellTextSelection={!isEditMode}
          stopEditingWhenCellsLoseFocus={true}
          loading={loading}
          rowHeight={40}
          headerHeight={42}
          groupHeaderHeight={36}
          pagination={true}
          paginationPageSize={pagination?.pageSize || 50}
          paginationPageSizeSelector={[25, 50, 100, 200]}
          suppressPaginationPanel={false}
        />
      </div>

      {/* Save Bar */}
      {changeCount > 0 && (
        <div className="spreadsheet-save-bar">
          <div className="change-count">
            <span className="dot" />
            <span>{changeCount} unsaved {changeCount === 1 ? 'change' : 'changes'}</span>
          </div>
          <div className="actions">
            <button
              className="btn-discard"
              onClick={handleDiscard}
              disabled={saving}
            >
              Discard
            </button>
            <button
              className="btn-save"
              onClick={handleSaveAll}
              disabled={saving}
            >
              {saving ? 'Saving...' : 'Save All'}
            </button>
          </div>
        </div>
      )}

      {/* Vital Signs Modal */}
      <Modal
        title="Vital Signs Entry"
        open={bpModalOpen}
        onOk={handleBpModalSave}
        onCancel={() => {
          setBpModalOpen(false);
          setBpModalRow(null);
          setBpModalReadings([]);
          setBpModalSelectedReadingKey('new');
        }}
        okText="Apply"
        cancelText="Cancel"
        width={380}
        centered
        className="entry-modal"
      >
        {bpModalRow && (
          <div className="entry-modal-body">
            <p className="entry-modal-patient">{bpModalRow.patientName}</p>
            <div className="entry-modal-field" style={{ marginBottom: 12 }}>
              <label>Select Reading</label>
              <Select
                value={bpModalSelectedReadingKey}
                style={{ width: '100%' }}
                onChange={(value) => {
                  setBpModalSelectedReadingKey(value);
                  if (value === 'new') {
                    setBpModalValues({ systolic: null, diastolic: null, heartRate: null });
                    return;
                  }
                  const selected = bpModalReadings.find(r => r.key === value);
                  setBpModalValues({
                    systolic: selected?.systolicBP || null,
                    diastolic: selected?.diastolicBP || null,
                    heartRate: selected?.heartRate || null,
                  });
                }}
                options={[
                  ...bpModalReadings.map((reading, idx) => ({
                    value: reading.key,
                    label: `Reading ${idx + 1}: ${reading.systolicBP}/${reading.diastolicBP}${reading.heartRate ? ` • HR ${reading.heartRate}` : ''}`,
                  })),
                  { value: 'new', label: '+ Add New Reading' },
                ]}
              />
            </div>
            <div className="entry-modal-row">
              <div className="entry-modal-field">
                <label>Systolic (mmHg)</label>
                <InputNumber
                  min={50}
                  max={300}
                  value={bpModalValues.systolic}
                  onChange={(v) => setBpModalValues(prev => ({ ...prev, systolic: v }))}
                  placeholder="120"
                  style={{ width: '100%' }}
                  size="large"
                />
              </div>
              <div className="entry-modal-field">
                <label>Diastolic (mmHg)</label>
                <InputNumber
                  min={30}
                  max={200}
                  value={bpModalValues.diastolic}
                  onChange={(v) => setBpModalValues(prev => ({ ...prev, diastolic: v }))}
                  placeholder="80"
                  style={{ width: '100%' }}
                  size="large"
                />
              </div>
            </div>
            <div className="entry-modal-field">
              <label>Heart Rate (bpm)</label>
              <InputNumber
                min={30}
                max={250}
                value={bpModalValues.heartRate}
                onChange={(v) => setBpModalValues(prev => ({ ...prev, heartRate: v }))}
                placeholder="72"
                style={{ width: '100%' }}
                size="large"
              />
            </div>
            <div className="entry-modal-preview">
              {bpModalValues.systolic && bpModalValues.diastolic
                ? `BP: ${bpModalValues.systolic}/${bpModalValues.diastolic} mmHg`
                : 'BP: —'}
              {bpModalValues.heartRate ? `  ·  HR: ${bpModalValues.heartRate} bpm` : ''}
            </div>
          </div>
        )}
      </Modal>

      {/* EKG Modal */}
      <Modal
        title="EKG Entry"
        open={ekgModalOpen}
        onOk={handleEkgModalSave}
        onCancel={() => { setEkgModalOpen(false); setEkgModalRow(null); }}
        okText="Apply"
        cancelText="Cancel"
        width={420}
        centered
        className="entry-modal"
      >
        {ekgModalRow && (
          <div className="entry-modal-body">
            <p className="entry-modal-patient">{ekgModalRow.patientName}</p>
            <div className="entry-modal-field">
              <label>Heart Rhythm</label>
              <Select
                value={ekgModalValues.heartRhythm}
                onChange={(v) => setEkgModalValues(prev => ({ ...prev, heartRhythm: v }))}
                placeholder="Select rhythm"
                style={{ width: '100%' }}
                size="large"
                allowClear
              >
                {RHYTHM_OPTIONS.map(opt => (
                  <Select.Option key={opt} value={opt}>{RHYTHM_LABELS[opt] || opt}</Select.Option>
                ))}
              </Select>
            </div>
            <div className="entry-modal-row">
              <div className="entry-modal-field">
                <label>QRS (ms)</label>
                <InputNumber
                  min={50}
                  max={300}
                  value={ekgModalValues.qrsDuration}
                  onChange={(v) => setEkgModalValues(prev => ({ ...prev, qrsDuration: v }))}
                  placeholder="80-120"
                  style={{ width: '100%' }}
                  size="large"
                />
              </div>
              <div className="entry-modal-field">
                <label>PR (ms)</label>
                <InputNumber
                  min={0}
                  max={500}
                  value={ekgModalValues.prInterval}
                  onChange={(v) => setEkgModalValues(prev => ({ ...prev, prInterval: v }))}
                  placeholder="120-200"
                  style={{ width: '100%' }}
                  size="large"
                />
              </div>
              <div className="entry-modal-field">
                <label>QTc (ms)</label>
                <InputNumber
                  min={200}
                  max={700}
                  value={ekgModalValues.qtcInterval}
                  onChange={(v) => setEkgModalValues(prev => ({ ...prev, qtcInterval: v }))}
                  placeholder="350-450"
                  style={{ width: '100%' }}
                  size="large"
                />
              </div>
            </div>
            <div className="entry-modal-field">
              <label>Interpretation</label>
              <Input.TextArea
                value={ekgModalValues.ekgInterpretation}
                onChange={(e) => setEkgModalValues(prev => ({ ...prev, ekgInterpretation: e.target.value }))}
                placeholder="e.g. Normal sinus rhythm, no acute changes"
                rows={2}
                style={{ borderRadius: 6 }}
              />
            </div>
            <div className="entry-modal-preview">
              {ekgModalValues.heartRhythm ? (RHYTHM_LABELS[ekgModalValues.heartRhythm] || ekgModalValues.heartRhythm) : '—'}
              {ekgModalValues.qrsDuration ? `  ·  QRS: ${ekgModalValues.qrsDuration}` : ''}
              {ekgModalValues.prInterval ? `  ·  PR: ${ekgModalValues.prInterval}` : ''}
              {ekgModalValues.qtcInterval ? `  ·  QTc: ${ekgModalValues.qtcInterval}` : ''}
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default SpreadsheetView;
